import { Map } from "immutable"
import 'intersection-observer'
import * as React from "react"
import { InView } from 'react-intersection-observer'
import { AsyncState, Fun, IOWidget, Updater, fromJSX } from "widgets-for-react"
import { CMSState, Id, ImageSize, Link } from "../../../../cms/blocks/types.gen"
import { getClassName, link } from "../../../../cms/blocks/utils.gen"
import { ModernImageContainer } from "../../../../cms/blocks/widgets/ImageContainer.gen"
import { blockInviewHandler } from "../../../../drupal/rendering/inview_utils.mod"
import { InnerHtmlWrapper } from "../../../../utils/InnerHtmlWrapper"
import { doubletextimagefirstblock_src, doubletextimagefirstblock_srcset, doubletextimagesecondblock_src, doubletextimagesecondblock_srcset } from "../../../utils/extra_images.mod"

export type ButtonProps = (
  | { kind: "button"; on_click: () => Updater<CMSState> }
  | Link) & { title: string }

export type DoubleTextImageBlockProps = {
  index: number
  imageLocation: "left" | "right"
  image: Id
  image_size: ImageSize
  image_alt_text: string
  secondary_image: Id
  secondary_image_size: ImageSize
  secondary_image_alt_text: string
  label?: string
  title: React.ReactNode
  subtitle?: React.ReactNode
  text: string
  secondary_text: string
  primary_button?: ButtonProps
  secondary_button?: ButtonProps
  link_type: boolean
  get_default_data_src: (() => ImageSize),
  get_default_data_srcset: (() => [ImageSize, string][])
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type DoubleTextImageBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  label_classname: string
  title_classname: string
  subtitle_classname: string
  text_classname: string
  intro_classname: string
  footer_classname: string
  clickable_wrapper_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
  figure_classname: string
  image_classname: string
}>

export function DoubleTextImageBlock(
  props: DoubleTextImageBlockProps,
  options?: { style?: DoubleTextImageBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return cmsState => fromJSX((setState) => (
          <InView
            as="section"
            onChange={(inView) => blockInviewHandler('textImage', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
            triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
            threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
            id={`io-${options && options.key ? options.key : props.index}`}
            className={`block block--text-image block--image-${props.imageLocation == "left" ? "left" : "right"} ls-block-spacing`}
            key={options && options.key ? options.key : `block-${props.index}`}
          >
            <div className="block__outer ls-container">
              <div className="block__inner">
                <div className="block__content">
                  <header className="block__header">
                    <h2 className="block__title title--l">{props.title}</h2>
                  </header>

                  {
                    // If our secondary button is set we render it inside an unordered list else we just place a single text_with_image component outside a list. This is to remove the botton line when there is only one image
                    (props.secondary_text && props.secondary_text.length > 0) || (props.secondary_button && props.secondary_button.kind == 'button' && props.secondary_button.title) ? 
                    <ul className='block__list blocklist'>
                      <li className='block__item blocklist__item'>
                        {text_with_image({ text: props.text, button: props.primary_button }, setState)}
                      </li>
                      <li className='block__item'>
                        {text_with_image({ text: props.secondary_text, button: props.secondary_button }, setState)}
                      </li>
                    </ul>
                      : 
                      (
                        (props.text && props.text.length > 0) || (props.primary_button && props.primary_button.kind == 'button' && props.primary_button.title) ? 
                          text_with_image({ text: props.text, button: props.primary_button }, setState)
                        : <></>
                      )
                    
                  }
                </div>

                <div className="block__visuals">
                  <figure className="block__figure">
                    <div className="block__visual fit-object">
                      {cmsState.images.has(props.image) &&
                        ModernImageContainer(
                          cmsState.images.get(props.image),
                          "block__image",
                          props.image_alt_text,
                          doubletextimagefirstblock_src,
                          doubletextimagefirstblock_srcset,
                          '(min-width: 1800px) 792px, (min-width: 1024px) 55vw, 65vw',
                          true,
                          '800',
                          '600'
                        ).run(_ => { })
                      }
                    </div>
                  </figure>
                  {
                    cmsState.images.has(props.secondary_image) ? 
                      <figure className="block__figure">
                      <div className="block__visual fit-object">
                      {
                        ModernImageContainer(
                            cmsState.images.get(props.secondary_image),
                            "block__image",
                            props.secondary_image_alt_text,
                            doubletextimagesecondblock_src,
                            doubletextimagesecondblock_srcset,
                            '(min-width: 1800px) 555px, 45vw',
                            true,
                            '555',
                            '555'
                          ).run(_ => { })  
                        }
                      </div>
                    </figure>
                    :
                    <></>
                  }
                </div>
              </div>
            </div>
          </InView>
        )  
    )
}


type TextWithImageProps = {
  text: string
  button: ButtonProps
}

const text_with_image = function (props: TextWithImageProps, onDone: (output: Fun<CMSState, CMSState>) => void) {
  return <>
    {
      props.text ? 
        //<p className='block__subtitle' dangerouslySetInnerHTML={{ __html: props.text }}></p>
        <InnerHtmlWrapper className="block__subtitle" text={props.text} />
        :
      <></>
    }
    {
      props.button ?
      (props.button.kind === "button" ? (
        <button
          className="block__link link"
          onClick={() => props.button.kind === "button" && onDone(props.button.on_click())}
        >
        {props.button.title}
        </button>
      ) : props.button.title && props.button.url
          ? (link(props.button,
            { classname: 'block__link link' },
            props.button.title,
            () => {
              window.dataLayer.push({
                event: `event.${sitename}`,
                category: 'DoubleImageTextBlock',
                action: props.button.title,
                label: window.location.pathname,
              })
            }))
          : null
        )
        :
        <></>
    }
  </>
}